import { useEffect, useState } from "react";
import { FileService } from "../../core/services/FileService";
import { _single } from "../../core/services/dataBean/EntityCatalog";
import { Alert } from "../theme/Component/Alert";

interface Props {
    url: string;
    type?: string | null; // ej: 'pdf', 'jpeg', 'png', 'jpg', 'svg', etc.
}

export const VisorAnyDocument = ({ url, type }: Props) => {
    const file = new FileService();
    const [urlBlob, setUrlBlob] = useState<string | null>(null);
    const [fetchFailed, setFetchFailed] = useState<boolean>(false);
    const visualTypes = ['pdf', 'png', 'jpg', 'jpeg', 'svg', 'gif', 'txt', 'html'];

    const getNormalizedType = (t?: string | null, u?: string) => {
        let typeToNormalize = t;
        if (!typeToNormalize && u) {
            const extension = u.split('.').pop()?.split('?')[0];
            if (extension) {
                typeToNormalize = extension;
            }
        }

        if (!typeToNormalize) return '';
        const lower = typeToNormalize.toLowerCase();
        if (lower.includes('pdf')) return 'pdf';
        if (lower.includes('png')) return 'png';
        if (lower.includes('jpeg') || lower.includes('jpg')) return 'jpeg';
        if (lower.includes('svg')) return 'svg';
        if (lower.includes('gif')) return 'gif';
        if (lower.includes('html')) return 'html';
        if (lower.includes('plain') || lower === 'txt') return 'txt';
        return lower.split('/').pop() || lower;
    };

    const normalizedType = getNormalizedType(type, url);

    useEffect(() => {
        if (!url) {
            console.warn('No URL provided.');
            setUrlBlob(null);
            setFetchFailed(false);
            return;
        }

        const fetchFile = async () => {
            setFetchFailed(false);
            if (!visualTypes.includes(normalizedType)) {
                // No intentamos fetch. Solo mostrar como descarga.
                setUrlBlob(null);
                return;
            }

            _single.spinner(true);
            try {
                const base64Data = await file.getPdfAsBase642(url);
                if (base64Data) {
                    const contentType = getMimeType(type || normalizedType);
                    const byteCharacters = atob(base64Data.split(',')[1]);
                    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: contentType });
                    const blobUrl = URL.createObjectURL(blob);
                    setUrlBlob(blobUrl);
                } else {
                    console.warn('No data received for the file.');
                    setUrlBlob(null);
                    setFetchFailed(true);
                }
            } catch (error) {
                console.error('Error loading file:', error);
                setUrlBlob(null);
                setFetchFailed(true);
            } finally {
                _single.spinner(false);
            }
        };

        fetchFile();

        return () => {
            if (urlBlob) {
                URL.revokeObjectURL(urlBlob);
            }
        };
    }, [url, type, normalizedType]);


    const getMimeType = (extensionOrMime?: string | null): string => {
        if (!extensionOrMime) return 'application/octet-stream';
        const lower = extensionOrMime.toLowerCase();
        if (lower.includes('/')) return lower; // Si ya es un mimetype válido

        switch (lower) {
            case 'pdf': return 'application/pdf';
            case 'png': return 'image/png';
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'svg': return 'image/svg+xml';
            case 'gif': return 'image/gif';
            case 'txt': return 'text/plain';
            case 'html': return 'text/html';
            default: return 'application/octet-stream'; // por defecto
        }
    };

    const renderPreview = () => {
        if (!urlBlob) return null;

        switch (normalizedType) {
            case 'pdf':
                return (
                    <iframe
                        title="PDF Viewer"
                        src={urlBlob}
                        style={{
                            width: '100%',
                            height: '100%',
                            minHeight: '80vh',
                            border: 'none',
                        }}
                    ></iframe>
                );

            case 'jpeg':
            case 'png':
            case 'jpg':
            case 'svg':
            case 'gif':
                return (
                    <img
                        src={urlBlob}
                        alt="Vista previa"
                        style={{ maxWidth: '100%', height: '90vh', objectFit: 'contain' }}
                    />
                );

            case 'txt':
            case 'html':
                return (
                    <iframe
                        title="Document Viewer"
                        src={urlBlob}
                        style={{ width: '100%', height: '500px', border: 'none' }}
                    ></iframe>
                );

            default:
                return (
                    <div>
                        <p>Este tipo de archivo no tiene vista previa.</p>
                        <a className="btn btn-outline-success" href={url} target="_blank" rel="noopener noreferrer">
                            Descargar Archivo
                        </a>
                    </div>
                );
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                {/* <p>{`${type} => ${url}`} <a href={url} target="_blank" rel="noopener noreferrer">Descargar</a></p> */}
                {urlBlob && !fetchFailed ? (
                    <div className="iframe-container">
                        {renderPreview()}
                    </div>
                ) : (
                    <Alert clase="warning">
                        <p>{fetchFailed ? "Hubo un problema al cargar la vista previa del documento." : "El documento no tiene vista previa."}</p>
                        {url &&
                            <a className="btn btn-outline-primary" href={url} target="_blank" rel="noopener noreferrer">
                                Descargar Archivo
                            </a>
                        }
                    </Alert>
                )}
            </div>
        </div>
    );
};
